// src/components/nexilink/score-bar.tsx
// Domain-specific progress bars built on shadcn/ui Progress
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export type ScoreColor = 'teal' | 'blue' | 'purple' | 'green' | 'orange' | 'red';

interface ScoreBarProps {
    label: string;
    score: number;
    maxScore: number;
    color?: ScoreColor;
    showPercentage?: boolean;
    className?: string;
}

const colorClasses: Record<ScoreColor, string> = {
    teal: '[&>div]:bg-[#1A4D4F]',
    blue: '[&>div]:bg-[#1A4D4F]',
    purple: '[&>div]:bg-[#1A4D4F]',
    green: '[&>div]:bg-green-500',
    orange: '[&>div]:bg-orange-500',
    red: '[&>div]:bg-red-500'
};

export function ScoreBar({
    label,
    score,
    maxScore,
    color = 'teal',
    showPercentage = true,
    className
}: ScoreBarProps) {
    const percentage = Math.round((score / maxScore) * 100);

    return (
        <div className={cn('flex items-center justify-between gap-3', className)}>
            <span className="text-sm text-muted-foreground min-w-[80px]">{label}</span>
            <div className="flex items-center gap-2 flex-1">
                <Progress
                    value={percentage}
                    className={cn('h-2', colorClasses[color])}
                />
                {showPercentage && (
                    <span className="text-sm font-semibold w-10 text-right">
                        {percentage}%
                    </span>
                )}
            </div>
        </div>
    );
}

// Simple percentage bar with label
interface ProgressBarProps {
    value: number; // 0-100
    label?: string;
    color?: ScoreColor;
    size?: 'sm' | 'md' | 'lg';
    showLabel?: boolean;
    className?: string;
}

export function ProgressBar({
    value,
    label,
    color = 'teal',
    size = 'md',
    showLabel = false,
    className
}: ProgressBarProps) {
    const sizeClasses = {
        sm: 'h-1.5',
        md: 'h-2',
        lg: 'h-3'
    };

    return (
        <div className={cn('w-full', className)}>
            {(showLabel || label) && (
                <div className="flex items-center justify-between mb-1">
                    {label && <span className="text-sm text-muted-foreground">{label}</span>}
                    {showLabel && <span className="text-sm font-semibold">{Math.round(value)}%</span>}
                </div>
            )}
            <Progress
                value={value}
                className={cn(sizeClasses[size], colorClasses[color])}
            />
        </div>
    );
}
